import React, { forwardRef } from 'react';
import classNames from 'classnames';
import './_flex.scss';
import PropTypes from 'prop-types';
import { marginClasses, paddingClasses, orderClasses, widthClasses } from '../../../utils/styles';
import { UI_NAME } from '../../../utils/config';

const Flex = forwardRef((props, ref) => {
  const {
    children,
    direction,
    justify,
    align,
    wrap,
    gap,
    grow,
    shrink,
    basis,
    className,
    style,
  } = props;

  const sanitizeValue = (value) => {
    if (typeof value === 'string') {
      return value
        .replace(/%/g, 'pct')
        .replace(/\s/g, '-')
        .toLowerCase();
    }
    return value;
  };

  const generateClasses = (propValue, prefix, sanitize = false) => {
    const classes = [];
    if (!propValue) return classes;

    const processValue = (value) => (sanitize ? sanitizeValue(value) : value);

    if (typeof propValue === 'object') {
      Object.entries(propValue).forEach(([breakpoint, value]) => {
        if (value != null) {
          const classSuffix = breakpoint === 'default'
            ? processValue(value)
            : `${breakpoint}-${processValue(value)}`;
          classes.push(`${UI_NAME}__flex--${prefix}-${classSuffix}`);
        }
      });
    } else {
      classes.push(`${UI_NAME}__flex--${prefix}-${processValue(propValue)}`);
    }

    return classes;
  };

  const classes = classNames(
    `${UI_NAME}__flex`,
    ...generateClasses(direction, 'direction'),
    ...generateClasses(justify, 'justify'),
    ...generateClasses(align, 'align'),
    ...generateClasses(wrap, 'wrap'),
    ...generateClasses(gap, 'gap'),
    ...generateClasses(grow, 'grow'),
    ...generateClasses(shrink, 'shrink'),
    ...generateClasses(basis, 'basis', true),
    ...orderClasses(props),
    ...widthClasses(props),
    ...marginClasses(props),
    ...paddingClasses(props),
    className,
  );

  return (
    <div ref={ref} className={classes} style={style}>
      {children}
    </div>
  );
});

Flex.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
    PropTypes.objectOf(
      PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
    ),
  ]),
  justify: PropTypes.oneOfType([
    PropTypes.oneOf(['start', 'end', 'center', 'between', 'around', 'evenly']),
    PropTypes.objectOf(
      PropTypes.oneOf(['start', 'end', 'center', 'between', 'around', 'evenly']),
    ),
  ]),
  align: PropTypes.oneOfType([
    PropTypes.oneOf(['start', 'end', 'center', 'stretch', 'baseline']),
    PropTypes.objectOf(
      PropTypes.oneOf(['start', 'end', 'center', 'stretch', 'baseline']),
    ),
  ]),
  wrap: PropTypes.oneOfType([
    PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
    PropTypes.objectOf(
      PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
    ),
  ]),
  gap: PropTypes.oneOfType([
    PropTypes.oneOf([2, 4, 8, 16, 'none']),
    PropTypes.number,
    PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.oneOf([2, 4, 8, 16, 'none']),
        PropTypes.number,
      ]),
    ),
  ]),
  grow: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.objectOf(PropTypes.number),
  ]),
  shrink: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.objectOf(PropTypes.number),
  ]),
  basis: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ),
  ]),
};

Flex.defaultProps = {
  gap: 'none',
  wrap: 'nowrap',
};

export default Flex;
